/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen., Ryan Duffy & Warren Dowey
 */

import { Injectable, OnDestroy } from '@angular/core';
import { AuthenticationService } from '../authentication/authentication.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { Logger } from '../logger/logger.service';
import { OrganisationUserService } from '@app/services/api-services/organisation-users/organisation-user.service';
import { ApiParamsModel } from '@app/models/api-params.model';
import { OrganisationUsersModel } from '@app/models/organisation-users.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpCacheService } from '../http/http-cache.service';
import { environment } from 'environments/environment';
const log = new Logger('TokenRefreshService');

@Injectable()
export class TokenRefreshService implements OnDestroy {
  private organisationUserSubscripion: Subscription;
  private timerCheck: any;
  private timerRefresh: any;
  private user: OrganisationUsersModel;

  // Subscriptions
  private logoutSubscription: Subscription;
  private lambaSubscription: Subscription;

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private organisationUserService: OrganisationUserService,
    private modalservice: NgbModal,
    private cacheService: HttpCacheService
  ) {}

  /**
   *  Warren Dowey
   *
   *  checkSessionIsActive
   *
   *  checks if the session is still active based on a provided timelimit in MS
   *
   * @param none
   *
   * @returns void checks active session
   */
  checkSessionIsActive() {
    log.info('AWS refresh token and status check triggered.');
    if (this.timerRefresh) {
      clearInterval(this.timerRefresh);
    }
    if (this.timerCheck) {
      clearInterval(this.timerCheck);
    }
    this.timerRefresh = <any>setInterval(() => {
      if (this.authenticationService.credentials) {
        this.refreshSession();
      }
    }, 240000);
    this.timerCheck = <any>setInterval(() => {
      if (this.authenticationService.credentials) {
        this.checkStatus();
      }
    }, 30000);
  }

  /**
   *  Warren Dowey
   *
   *  RefreshSession
   *
   *  Calls the CheckUserOnline Lambda, which returns if the user is deemed "ACTIVE" or "INACTIVE"
   * Refreshes JWT Cognito Tokens, by getting and refreshing tokens
   *
   * @param none
   *
   * @returns void refreshes user session
   */
  refreshSession() {
    if (this.authenticationService.credentials) {
      log.debug('refreshing token');
      this.authenticationService.cognitoUserRefreshSession();
    }
  }

  /**
   *  Warren Dowey
   *
   *  checkStatus
   *
   *  Checks if the session is still active if not kicks the user out
   *
   * @param none
   *
   * @returns void: kicks out the user or nothing
   */
  checkStatus() {
    if (this.authenticationService.user) {
      this.organisationUserSubscripion = this.organisationUserService.getOrganisationUserByUserId(this.authenticationService.user.userId).subscribe(
        (user) => {
          if (this.organisationUserSubscripion) {
            this.organisationUserSubscripion.unsubscribe();
          }
          if (!!user) {
            this.user = user;
            if (this.user.sessionId !== this.authenticationService.sessionId) {
              log.debug(
                'kicking out: USID:' +
                  this.user.sessionId +
                  ' ASSID:' +
                  this.authenticationService.sessionId
              );
              this.kickOut('Another session has been activated for this user.');
              this.modalservice.dismissAll();
            }
          }
        }
      );
    }
  }

  /**
   *  Warren Dowey
   *
   *  kickOut
   *
   *  Kicks a user out by removing their credentials and provides a reason for it
   *
   * @param reason for being kicked out this is optional
   *
   * @returns void: kicks the user out
   */
  private kickOut(reason?) {
    clearInterval(this.timerRefresh);
    clearInterval(this.timerCheck);
    this.logoutSubscription = this.authenticationService.logout().subscribe(() =>
      this.router.navigate(['/login'], {
        queryParams: {
          reason: reason,
        },
        replaceUrl: true,
      })
    );
  }

  ngOnDestroy(): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }
    if (this.lambaSubscription) {
      this.lambaSubscription.unsubscribe();
    }
  }
}
